import React from "react";
import { Modal, Button, Input, Row, Col, Typography, message, Divider, Space } from "antd";
import { 
    CopyOutlined, 
    GlobalOutlined, 
    ReloadOutlined, 
    EyeOutlined, 
    MailOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";

const { Text, Title } = Typography;

const ShareReportModal = ({ visible, onCancel, shareLink, onGenerateNewLink, onViewReport, reportTitle, isSyncing }) => {
    const [copied, setCopied] = React.useState(false);
    
    const handleCopyLink = () => {
        if (shareLink) {
            navigator.clipboard.writeText(shareLink)
                .then(() => {
                    setCopied(true);
                    message.success('Link copied to clipboard!');
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(err => {
                    console.error('Could not copy text: ', err);
                    message.error('Failed to copy link');
                });
        }
    };

    const mailtoLink = `mailto:?subject=${encodeURIComponent(`Photo Report: ${reportTitle}`)}&body=${encodeURIComponent(`You can view the photo report here: ${shareLink}`)}`;

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
            centered
            className="share-report-modal"
            bodyStyle={{ padding: '0px' }}
            closeIcon={<span style={{ fontSize: '18px', color: '#8c8c8c' }}>×</span>}
        >
            {/* Header */}
            <div style={{ padding: '24px 32px 16px' }}>
                <Title level={3} style={{ margin: 0, fontWeight: 700, fontSize: '24px' }}>
                    Share Your Report
                </Title>
            </div>
            
            <Divider style={{ margin: 0 }} />

            {/* Content Area */}
            <div style={{ padding: '32px' }}>
                <Row gutter={12} align="middle" wrap={false}>
                    <Col flex="auto">
                        <Input 
                            prefix={<GlobalOutlined style={{ color: '#bfbfbf' }} />}
                            value={isSyncing ? "Saving secure link..." : shareLink}
                            readOnly
                            style={{ 
                                height: '48px', 
                                borderRadius: '8px', 
                                backgroundColor: isSyncing ? '#fafafa' : '#f5f5f5', 
                                border: '1px solid #d9d9d9',
                                fontSize: '15px'
                            }}
                        />
                    </Col>
                    <Col>
                        <Button 
                            type="link" 
                            icon={isSyncing ? null : (copied ? null : <CopyOutlined />)} 
                            onClick={handleCopyLink}
                            disabled={isSyncing}
                            style={{ 
                                height: '48px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                fontWeight: 600,
                                fontSize: '16px',
                                color: isSyncing ? '#bfbfbf' : (copied ? '#52c41a' : '#0060df'),
                                width: '100px',
                                justifyContent: 'flex-start'
                            }}
                        >
                            {isSyncing ? 'Wait...' : (copied ? 'Copied' : 'Copy Link')}
                        </Button>
                    </Col>
                </Row>
                
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ClockCircleOutlined style={{ color: '#0060df', fontSize: '16px' }} />
                    <Text type="secondary" style={{ fontSize: '14px', color: '#595959' }}>
                        This link will expire in 30 days (1 month)
                    </Text>
                </div>
            </div>

            {/* Bottom Gray Bar Area */}
            <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '24px 32px', 
                borderBottomLeftRadius: '8px', 
                borderBottomRightRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Button 
                    type="link" 
                    icon={<ReloadOutlined />} 
                    onClick={onGenerateNewLink}
                    style={{ 
                        color: '#595959', 
                        padding: 0, 
                        height: 'auto',
                        fontWeight: 600,
                        fontSize: '15px'
                    }}
                >
                    Generate New Link
                </Button>

                <Space size={12}>
                    <Button 
                        icon={<EyeOutlined />} 
                        onClick={onViewReport}
                        style={{ 
                            height: '44px', 
                            padding: '0 20px', 
                            borderRadius: '8px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#ecedf0',
                            border: 'none'
                        }}
                    >
                        View Report
                    </Button>
                    <a 
                        href={mailtoLink}
                        className="ant-btn ant-btn-primary"
                        style={{ 
                            height: '44px', 
                            padding: '0 24px', 
                            borderRadius: '8px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#0060df',
                            boxShadow: '0 2px 4px rgba(0, 96, 223, 0.2)',
                            color: 'white',
                            lineHeight: '44px'
                        }}
                    >
                        <MailOutlined style={{ marginRight: '8px' }} />
                        Email
                    </a>
                </Space>
            </div>
        </Modal>
    );
};

export default ShareReportModal;
